import { observable, action, runInAction, toJS } from 'mobx';
import localforage from 'localforage';
import { isEmpty, cloneDeep } from 'lodash';
import Cookies from 'js-cookie';
import posthog from 'posthog-js';

import Service from '@service/index';
import moStore from '@store/index';
import { PosthogApiKey, PosthogApiHost } from '@/config';

interface IItem {
  code: string,
  name: string,
}

const moUser = observable({
  loginHidden: true,
  isLogin: false, // 是否登录
  isVip: false, // 是否会员
  isGolden: false, // 是否黄金会员
  reportCount: 0,
  job: '',
  msg: '', // 未读消息数
  industry: <IItem>{ code: '', name: '' },
  city: <IItem>{ code: '', name: '' },
  nature: <IItem>{ code: '', name: '' },
  scale: <IItem>{ code: '', name: '' },
  company: '',
  // 可查询职位的最大值，未登录默认为5，vip为50
  queryMax: 5,
  isExpired: false,
  // 判断是否有定制薪酬报告
  isStructureExpired: false,
  isReportExpired: false,
  isAnalysisExpired: false,
  isEfficiencyExpired: false,
  isIndicatorExpired: false,
  isMarketExpired: false,
  isDataBoardExpired: false,
  userInfo: <any>null, // 用户信息
  saleInfo: <any>null,
  phone:'',
  useruuid:'',
});

// 初始化用户信息
const resetUser = action(() => {
  moUser.loginHidden = true;
  moUser.isLogin = false;
  moUser.isVip = false;
  moUser.isGolden = false;
  moUser.reportCount = 0;
  moUser.job = '';
  moUser.msg = '';
  moUser.industry = { code: '', name: '' };
  moUser.city = { code: '', name: '' };
  moUser.nature = { code: '', name: '' };
  moUser.scale = { code: '', name: '' };
  moUser.company = '';
  moUser.queryMax = 5;
  moUser.isExpired = false;
  moUser.isStructureExpired = false;
  moUser.isReportExpired = false;
  moUser.isAnalysisExpired = false;
  moUser.isEfficiencyExpired = false;
  moUser.isIndicatorExpired = false;
  moUser.isMarketExpired = false;
  moUser.userInfo = null;
  moUser.saleInfo = null;
  moUser.phone = '';
  moUser.useruuid = '';
});

// 获取用户信息
const getUserInfo = action(async (callback: () => void) => {
  const res: any = await Service.user();

  if (isEmpty(res)) {
    runInAction(() => {
      moUser.loginHidden = false;
      callback();
    });
  } else {
    posthog.identify(
      res.user_uuid,
      {
        email: res.email || '-',
        phone: res.phone || '-',
        company: res.company || '-',
        company_uuid: res.company_uuid || '-',
        role_name: res.role_name || '-',
        last_login_time: res.last_login_time || '-',
        user_industry: res.user_industry || '-',
        user_city: res.user_city || '-',
        user_job: res.user_job || '-',
        user_nature: res.user_nature || '-',
        user_scale: res.user_scale || '-',
        level: res.level,
      },
    );

    runInAction(() => {
      moUser.loginHidden = false;
      moUser.isLogin = true;
      moUser.isVip = !res.is_expired;
      moUser.phone = res.phone || '';
      moUser.useruuid = res.user_uuid || '';
      moUser.reportCount = res.custom_report_count || 0;
      moUser.isStructureExpired = res.is_structure_expired;
      moUser.isReportExpired = res.is_report_expired;
      moUser.isAnalysisExpired = res.is_analysis_expired;
      moUser.isEfficiencyExpired = res.is_efficiency_expired;
      moUser.isIndicatorExpired = res.is_indicator_expired;
      moUser.isMarketExpired = res.is_market_expired;
      moUser.isDataBoardExpired = res.is_data_board_expired;
      moUser.isExpired = res.is_expired;
      if (!moUser.isMarketExpired) moUser.queryMax = 50;
      moUser.job = res.user_job;
      moUser.msg = res.un_read_msg_count || '';

      if (!isEmpty(res.user_industry) && res.user_industry?.code && res.user_industry?.name){
        moUser.industry.code = res.user_industry?.code || '';
        moUser.industry.name = res.user_industry?.name || '';
      }
      if (!isEmpty(res.user_city) && res.user_city?.code && res.user_city?.name){
        moUser.city.code = res.user_city?.code || '';
        moUser.city.name = res.user_city?.name || '';
      }
      if (!isEmpty(res.user_nature) && res.user_nature?.code && res.user_nature?.name){
        moUser.nature.code = res.user_nature?.code || '';
        moUser.nature.name = res.user_nature?.name || '';
      }
      if (!isEmpty(res.user_scale) && res.user_scale?.code && res.user_scale?.name){
        moUser.scale.code = res.user_scale?.code || '';
        moUser.scale.name = res.user_scale?.name || '';
      }
      moUser.company = res.company || '';
      moUser.userInfo = res;
      moUser.saleInfo = res.saleInfo;

      callback();
    });
  }
});

// 获取基础信息
const getBaseInfo = action(async (callback: () => void) => {
  resetUser();
  const res: any = await Service.baseInfo();

  runInAction(() => {
    moUser.industry = res?.industry || { code: '', name: '' };
    moUser.company = res?.company || '';
    moUser.city = res?.city || { code: '', name: '' };
    moUser.nature = res?.nature || { code: '', name: '' };
    moUser.scale = res?.scale || { code: '', name: '' };

    getUserInfo(callback);
  });
});

export default moUser;
export { getBaseInfo, resetUser };
